const asList = value => Array.isArray(value) ? value : value ? [value] : []
const optionPath = (parent, option, index) => [...parent, option.id != null ? `id:${option.id}` : `index:${index}`]

 
export const buildQuestionOptionList = (options, parentPath = [], depth = 0) =>
  (Array.isArray(options) ? options : []).flatMap((option, index) => {
    const path = optionPath(parentPath, option, index)
    const label = (option.label || '').trim()
    return [
      { value: label, label, selectionKey: JSON.stringify(path), parentSelectionKey: parentPath.length ? JSON.stringify(parentPath) : null, depth, option },
      ...buildQuestionOptionList(option.options, path, depth + 1),
    ]
  })

export const getSelectedQuestionOptions = (question, choices = buildQuestionOptionList(question.options)) => {
  if (Array.isArray(question.selectedOptionPaths)) {
    const keys = new Set(question.selectedOptionPaths)
    return choices.filter(choice => keys.has(choice.selectionKey))
  }
  const values = Array.isArray(question.answer) ? question.answer : (question.answer || '').split('|||')
 
  return values.filter(value => typeof value === 'string' && value.trim()).map(value =>
    choices.find(choice => choice.depth === 0 && choice.value === value.trim()) ||
    choices.find(choice => choice.value === value.trim())
  ).filter(Boolean)
}

export const getQuestionOptionSelectionPatch = (question, selected) => {
  const entries = asList(selected)
  const patch = { answer: question.isMultiple ? entries.map(entry => entry.value).join('|||') : entries[0]?.value || '' }
  if (buildQuestionOptionList(question.options).some(choice => choice.depth > 0) || Array.isArray(question.selectedOptionPaths)) {
    patch.selectedOptionPaths = entries.map(entry => entry.selectionKey)
  }
  return patch
}

 
export const getActiveQuestionOptions = (question, choices = buildQuestionOptionList(question.options)) => {
  const keys = new Set()
  for (const selected of getSelectedQuestionOptions(question, choices)) {
    const path = JSON.parse(selected.selectionKey)
    for (let length = 1; length <= path.length; length++) keys.add(JSON.stringify(path.slice(0, length)))
  }
  return choices.filter(choice => keys.has(choice.selectionKey))
}

const isInBranch = (key, parentKey) => {
  const path = JSON.parse(key)
  const parent = JSON.parse(parentKey)
  return parent.length <= path.length && parent.every((segment, index) => path[index] === segment)
}

const nestedSelectionPatch = (question, choices, keys) => {
  const selected = choices.filter(choice => keys.has(choice.selectionKey))
  return {
    answer: selected.map(choice => choice.value).join('|||'),
    selectedOptionPaths: selected.map(choice => choice.selectionKey),
  }
}

export const selectQuestionParentOptions = (question, selected) => {
  const choices = buildQuestionOptionList(question.options)
  if (!choices.some(choice => choice.depth > 0)) return getQuestionOptionSelectionPatch(question, selected)
  const parents = asList(selected).filter(choice => choice.depth === 0)
  const parentKeys = new Set(parents.map(choice => choice.selectionKey))
  const keys = new Set(parentKeys)
  for (const child of getActiveQuestionOptions(question, choices)) {
    const rootKey = JSON.stringify(JSON.parse(child.selectionKey).slice(0, 1))
    if (child.depth > 0 && parentKeys.has(rootKey)) keys.add(child.selectionKey)
  }
  return nestedSelectionPatch(question, choices, keys)
}

export const toggleQuestionChildOption = (question, selectionKey) => {
  const choices = buildQuestionOptionList(question.options)
  const child = choices.find(choice => choice.selectionKey === selectionKey && choice.depth > 0)
  const active = getActiveQuestionOptions(question, choices)
  const keys = new Set(active.map(choice => choice.selectionKey))
  if (!child || !keys.has(child.parentSelectionKey)) return {}
  if (keys.has(child.selectionKey)) {
    for (const key of keys) if (isInBranch(key, child.selectionKey)) keys.delete(key)
  } else {
    const parent = choices.find(choice => choice.selectionKey === child.parentSelectionKey)
    if (!parent.option.isMultiple) {
      for (const sibling of choices.filter(choice => choice.parentSelectionKey === child.parentSelectionKey)) {
        for (const key of keys) if (isInBranch(key, sibling.selectionKey)) keys.delete(key)
      }
    }
    keys.add(child.selectionKey)
  }
  return nestedSelectionPatch(question, choices, keys)
}

